import { useEffect, useMemo, useState } from '@wordpress/element';
import GiveawayStickerBundle, { getTokenId, getPartnerId } from './GiveawayStickerBundle';
import { useGiveaway } from './GiveawayProvider';
import { requestSlotReward } from './giveawayApi';

const X_POSITIONS = ['left', 'middle', 'right'];
const Y_POSITIONS = ['top', 'middle', 'bottom'];

function hashString(value) {
    let hash = 0;

    for (let i = 0; i < value.length; i++) {
        hash = ((hash << 5) - hash) + value.charCodeAt(i);
        hash |= 0;
    }

    return Math.abs(hash);
}

function normalizePlacements(placement) {
    return (Array.isArray(placement) ? placement : [placement]).filter(Boolean);
}

function getPosition(slot, placements) {
    const today = new Date().toISOString().slice(0, 10);
    const first = placements?.[0] || {};
    const seed = `${today}:${slot}:${getTokenId(first)}:${getPartnerId(first)}:${placements.length}`;
    const hash = hashString(seed);

    return {
        x: X_POSITIONS[hash % X_POSITIONS.length],
        y: Y_POSITIONS[Math.floor(hash / 7) % Y_POSITIONS.length],
    };
}

export default function GiveawaySlotSticker({
    placement,
    slot,
    sourceType = 'homepage',
    label = 'Sticker',
}) {
    const { openToken } = useGiveaway();
    const [hiddenTokenIds, setHiddenTokenIds] = useState([]);
    const [claiming, setClaiming] = useState(false);
    const [error, setError] = useState('');

    const placements = useMemo(() => normalizePlacements(placement), [placement]);
    const visiblePlacements = useMemo(
        () => placements.filter((item) => !hiddenTokenIds.includes(String(getTokenId(item)))),
        [placements, hiddenTokenIds]
    );

    const position = useMemo(
        () => getPosition(slot, placements),
        [slot, placements]
    );

    useEffect(() => {
        const hideIfCollected = (event) => {
            const detail = event.detail || {};

            if (detail.sourceSlot !== slot || detail.sourceType !== sourceType) {
                return;
            }

            const tokenIds = Array.isArray(detail.tokenIds)
                ? detail.tokenIds
                : [detail.tokenId || detail.id].filter(Boolean);

            if (!tokenIds.length) {
                setHiddenTokenIds(placements.map((item) => String(getTokenId(item))));
                return;
            }

            setHiddenTokenIds((current) => {
                const next = new Set(current);
                tokenIds.forEach((tokenId) => next.add(String(tokenId)));
                return [...next];
            });
        };

        window.addEventListener('bjnt:giveaway-collected', hideIfCollected);
        window.addEventListener('bjnt:giveaway-refresh', hideIfCollected);

        return () => {
            window.removeEventListener('bjnt:giveaway-collected', hideIfCollected);
            window.removeEventListener('bjnt:giveaway-refresh', hideIfCollected);
        };
    }, [slot, sourceType, placements]);

    if (!visiblePlacements.length) return null;

    return (
        <div
            className={[
                'bjnt-giveaway-slot-wrap',
                `bjnt-giveaway-slot-wrap--x-${position.x}`,
                `bjnt-giveaway-slot-wrap--y-${position.y}`,
                visiblePlacements.length > 1 ? 'bjnt-giveaway-slot-wrap--grouped' : '',
            ].join(' ')}
        >
            <GiveawayStickerBundle
                tokens={visiblePlacements}
                variant="slot"
                disabled={claiming}
                label={label}
                onClick={async () => {
                    if (claiming) return;

                    setClaiming(true);
                    setError('');

                    try {
                        const rewardResults = await Promise.allSettled(
                            visiblePlacements.map(async (item) => {
                                const tokenId = getTokenId(item);
                                const reward = await requestSlotReward(slot, sourceType, tokenId);
                                return { item, tokenId, reward };
                            })
                        );

                        const rewards = rewardResults
                            .map((result, index) => {
                                if (result.status === 'fulfilled') return result.value;

                                return {
                                    item: visiblePlacements[index],
                                    tokenId: getTokenId(visiblePlacements[index]),
                                    reward: null,
                                };
                            });

                        if (rewardResults.some((result) => result.status === 'rejected')) {
                            setError('Some stickers could not be loaded. Please try again.');
                        }

                        const collectedOrUnavailableIds = [];
                        const tokens = [];

                        rewards.forEach(({ item, tokenId, reward }) => {
                            if (reward?.alreadyCollected || reward?.unavailable) {
                                collectedOrUnavailableIds.push(String(tokenId));
                                return;
                            }

                            if (!reward?.success || !reward?.token) return;

                            tokens.push({
                                ...reward.token,
                                slot,
                                sourceSlot: slot,
                                sourceType,
                                tokenId: reward.token.tokenId || reward.token.id || tokenId,
                                partnerId: reward.token.partnerId || getPartnerId(item),
                            });
                        });

                        if (collectedOrUnavailableIds.length) {
                            setHiddenTokenIds((current) => [
                                ...new Set([...current, ...collectedOrUnavailableIds]),
                            ]);
                        }

                        if (!tokens.length) {
                            if (!collectedOrUnavailableIds.length) {
                                setError('This sticker could not be loaded. Please try again.');
                            }
                            return;
                        }

                        openToken({
                            ...tokens[0],
                            tokens,
                            packId: `${slot}:${tokens.map((token) => token.claimToken).join('|')}`,
                            slot,
                            sourceSlot: slot,
                            sourceType,
                        });
                    } finally {
                        setClaiming(false);
                    }
                }}
            />
            {error ? <span className="bjnt-giveaway-slot-error" role="status">{error}</span> : null}
        </div>
    );
}
